import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { Sentiments } from 'src/sb-modules/Frontend-form-field-module/common/enums/sentiments.enum';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { AttachmentBase } from 'src/sb-tools/typescript/models/attachment-base.model';
import { ClientForm } from './client-form.model';
import { ClientField } from './client-field.model';
import { FieldValue } from './field-value.model';
import { FormType } from '@sb-tools/typescript/enums/form-type.enum';
import { DetailedTicketSource } from '@sb-tools/typescript/enums/detailed-ticket-source.enum';
import { FieldRulesValidationService } from '@form-field-module/common/services/field-rules-validation.service';

export class AddTicketModel {
    address: string;

    attachments: AttachmentBase[];

    categoryId?: number;

    bcc: string;

    cc: string;

    email: string;

    fields: FieldValue[];

    formId?: number;

    forwardedTicketId: number;

    isAssignedToCreator: boolean;

    isSendFields: boolean; // gui only

    isSendToClient: boolean;

    isTextContent: boolean;

    isTicketSentAsLink: boolean;

    message: string;

    name: string;

    phoneNumber: string;

    sentiment: number;

    tags: string[];

    ticketSource: DetailedTicketSource;

    title: string;

    status?: TicketStatus;

    constructor(form?: ClientForm, ticketStatus?: TicketStatus, fieldsValidator?, isTicketSentAsLink?: boolean) {
        this.isTicketSentAsLink = isTicketSentAsLink;
        this.status = ticketStatus;

        if (form) {
            form.fields = AddTicketModel.setMapToTickedForDependedFields(form.fields);
            this.formId = form.id;
            this.email = AddTicketModel.getFormElementValue(form.fields, FieldType.userIdentification);
            this.name = AddTicketModel.getFormElementValue(form.fields, FieldType.userInfo);
            this.title = AddTicketModel.getFormElementValue(form.fields, FieldType.title);
            this.message = AddTicketModel.getFormElementValue(form.fields, FieldType.message);
            const sentiment = AddTicketModel.getFormElementValue(form.fields, FieldType.sentiment);
            this.sentiment = sentiment ? sentiment : Sentiments.question;
            this.categoryId = AddTicketModel.getFormElementValue(form.fields, FieldType.categories);
            this.fields = AddTicketModel.getFields(form.fields, fieldsValidator);
            this.attachments = AddTicketModel.getFormElementValue(form.fields, FieldType.attachment) || [];
            this.ticketSource = DetailedTicketSource.formExternal;
            this.isSendToClient = AddTicketModel.getFormElementValue(
                form.fields,
                FieldType.sendCopyToTheClientCheckbox,
            );
            this.cc = AddTicketModel.getFormElementValue(form.fields, FieldType.ccUsers);
            this.bcc = AddTicketModel.getFormElementValue(form.fields, FieldType.bccUsers);

            // @todo check why 'isTextContent' is always set to false for internal forms?
            this.isTextContent =
                form.type === FormType.webInternal ? false : this.isMessageContentTypeText(form.fields);
            this.isSendFields =
                AddTicketModel.getFormElementValue(form.fields, FieldType.sendFieldsToTheClientCheckbox) || false;
            this.tags = this.getBamaTag(form.fields);
        }
    }

    static checkHiddenElementsValues(fields, checkAndSetValidValues): void {
        fields.forEach((field) => {
            if (field.isHidden && field.value && field.value === field.defaultValue) {
                field.value = null;

                checkAndSetValidValues(field, field.defaultValue);
            }
        });
    }

    static getNotEmptyValues(customFields) {
        return customFields.filter((formField) => {
            return formField.value;
        });
    }

    static isNeedToSetFieldValue(formField: ClientField): boolean {
        return (
            !formField.isDependant ||
            (formField.isDependant && (formField.isShowedBecauseOfRule || formField.isMapToTickedModel))
        );
    }

    static processStoreInfoElementValue(fields, processedFields) {
        const storeInfoElement = fields.find((formField) => {
            return formField.type === FieldType.storeInfo;
        });

        if (storeInfoElement && storeInfoElement.valueModel[0]) {
            storeInfoElement.valueModel = storeInfoElement.valueModel.filter((value) => {
                return value != null;
            });

            storeInfoElement.value = JSON.stringify(storeInfoElement.valueModel);

            processedFields.push(storeInfoElement);
        }
    }

    static getFields(formFields: ClientField[], fieldsValidator?): FieldValue[] {
        let fields;

        fields = formFields.filter((formField) => {
            return (
                AddTicketModel.isNeedToSetFieldValue(formField) &&
                !formField.isBasic &&
                formField.value &&
                formField.type !== FieldType.formattedText &&
                !(formField.type === FieldType.checkbox && formField.options?.isConfirmation === 'true')
            );
        });

        AddTicketModel.processStoreInfoElementValue(formFields, fields);

        if (fieldsValidator && typeof fieldsValidator === 'function') {
            AddTicketModel.checkHiddenElementsValues(fields, fieldsValidator);
            fields = this.getNotEmptyValues(fields);
        }

        fields = fields.map((field) => {
            return {
                id: field.id,
                value: field.value,
            };
        });

        return fields;
    }

    static getFormElementValue(fields: ClientField[], elementType: FieldType) {
        const formField = fields.find((element) => {
            return element.type === elementType;
        });

        if (formField && AddTicketModel.isNeedToSetFieldValue(formField)) {
            if (
                formField.type === FieldType.sendCopyToTheClientCheckbox ||
                formField.type === FieldType.sendFieldsToTheClientCheckbox
            ) {
                return formField.value || false;
            } else {
                return formField.value;
            }
        }

        return null;
    }

    static setMapToTickedForDependedFields(fields: ClientField[]): ClientField[] {
        fields.forEach((field: ClientField) => {
            if (field.isHidden && field.rules) {
                this.setMapToTickedRecursively(fields, field);
            }
        });
        return fields;
    }

    private static setMapToTickedRecursively(fields: ClientField[], field: ClientField) {
        field.rules.forEach((rule) => {
            if (FieldRulesValidationService.isFieldValueMatchRule(rule, field)) {
                const dependantFormField: ClientField = fields.find((formField: ClientField) => {
                    return formField.id === rule.dependantFieldId;
                });

                if (dependantFormField.defaultValue) {
                    dependantFormField.isMapToTickedModel = true;
                    if (dependantFormField.rules) {
                        this.setMapToTickedRecursively(fields, dependantFormField);
                    }
                }
            }
        });
    }

    private getBamaTag(fields) {
        const storeInfoFormElement = fields.find((formField) => {
            return formField.type === FieldType.storeInfo;
        });

        if (storeInfoFormElement && storeInfoFormElement.valueModel[0]) {
            return [storeInfoFormElement.valueModel[0]];
        } else {
            return [];
        }
    }

    private isMessageContentTypeText(fields): boolean {
        const formMessageElement = fields.find((element) => {
            return element.type === FieldType.message;
        });

        if (formMessageElement) {
            return formMessageElement.options.isHtmlEditor === 'false' || !formMessageElement.options.isHtmlEditor;
        } else {
            return false;
        }
    }
}
